import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Login from './Login';
import './ProgressiveSignupPrompt.css';

const TIME_THRESHOLD_MS = 60 * 1000;
const CLICK_THRESHOLD = 17;

const EXCLUDED_PATH_PREFIXES = [
  '/admin',
  '/dashboard',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/checkout',
  '/subscribe',
  '/profile',
  '/bookings',
  '/courses/player',
  '/terms',
  '/privacy',
];

const isExcludedPath = (pathname) => {
  return EXCLUDED_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const isIdleGateOpen = () => document.body.classList.contains('auth-idle-gate-open');

const ProgressiveSignupPrompt = () => {
  const { isLoggedIn, isLoading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [hasPromptedThisVisit, setHasPromptedThisVisit] = useState(false);
  const dialogRef = useRef(null);
  const clickCountRef = useRef(0);

  const shouldTrackOnPath = useMemo(() => {
    return !isExcludedPath(location.pathname);
  }, [location.pathname]);

  const canShowPrompt = useCallback(() => {
    return (
      !isLoading &&
      !isLoggedIn &&
      shouldTrackOnPath &&
      !isPromptOpen &&
      !hasPromptedThisVisit &&
      !isIdleGateOpen()
    );
  }, [
    hasPromptedThisVisit,
    isLoading,
    isLoggedIn,
    isPromptOpen,
    shouldTrackOnPath,
  ]);

  const openPrompt = useCallback(() => {
    if (!canShowPrompt()) return;

    setIsPromptOpen(true);
    setHasPromptedThisVisit(true);
  }, [canShowPrompt]);

  const handleLoginSuccess = useCallback(() => {
    setIsPromptOpen(false);
    clickCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (!canShowPrompt()) return undefined;

    const handleGuestClick = () => {
      clickCountRef.current += 1;
      if (clickCountRef.current >= CLICK_THRESHOLD) {
        openPrompt();
      }
    };

    document.addEventListener('pointerdown', handleGuestClick, true);

    return () => {
      document.removeEventListener('pointerdown', handleGuestClick, true);
    };
  }, [
    canShowPrompt,
    openPrompt,
  ]);

  useEffect(() => {
    if (!canShowPrompt()) return undefined;

    const timer = window.setTimeout(() => {
      if (canShowPrompt()) {
        openPrompt();
      }
    }, TIME_THRESHOLD_MS);

    return () => window.clearTimeout(timer);
  }, [canShowPrompt, openPrompt]);

  useEffect(() => {
    if (!isPromptOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isPromptOpen]);

  if (isLoading || isLoggedIn || !shouldTrackOnPath) {
    return null;
  }

  return (
    <AnimatePresence>
      {isPromptOpen && (
        <motion.div
          className="progressive-signup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.section
            className="progressive-signup-dialog progressive-signup-page-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={t('auth.progressiveSignup.title', {}, 'Save your spiritual progress')}
            tabIndex="-1"
            ref={dialogRef}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={(event) => event.stopPropagation()}
          >
            <Login
              onLoginSuccess={handleLoginSuccess}
              redirectMessage={t(
                'auth.progressiveSignup.loginPrompt',
                {},
                'Sign in or continue with Google to save your progress.'
              )}
            />
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProgressiveSignupPrompt;
